import React, { useMemo } from 'react';
import { Box, Skeleton, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

const ChartSkeleton = ({ height = 200 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // ✅ UPDATED: More aggressive bar count reduction for ultra-small screens
  const getBarCount = () => {
    if (isMobile) return 4;  // Even more compact: 4 bars for <400px viewports
    if (isTablet) return 8;  // Reduced from 12 to prevent squeeze
    return 16;  // Reduced from 24 for better fit without cutoff
  };

  const barCount = getBarCount();
  const barWidth = isMobile ? 16 : (isTablet ? 20 : 24);  // Smaller base widths to fit tighter

  // ✅ NEW: Reduced gap for large screens (desktop) to add less space between bars
  const getGapValue = () => {
    if (isMobile) return 0.5;
    if (isTablet) return 0.75;
    return 0.25;  // Less space on desktop/large screens
  };

  const gapValue = getGapValue();

  const barHeightsPx = useMemo(() => {
    return Array.from({ length: barCount }, (_, i) => {
      const t = barCount > 1 ? i / (barCount - 1) : 0;
      const wave = 0.5 + 0.5 * Math.sin(t * Math.PI * 2 * 1.4 + 0.35);
      const h = height * (0.24 + wave * 0.58);
      return Math.max(8, Math.round(h));
    });
  }, [barCount, height]);

  return (
    <Box sx={{
      position: "relative",
      height: height + 60,
      width: '100%',
      overflow: 'hidden'
    }}>
      {/* ✅ FIXED: Y-axis – stable sizing */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: { xs: 28, sm: 40 },
          height: height,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start",
          paddingRight: { xs: "4px", sm: "12px" },
          zIndex: 1,
        }}
      >
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton
            key={i}
            variant="text"
            width={16}
            height={14}
            sx={{
              fontSize: { xs: "10px", sm: "12px" },
              width: { xs: 14, sm: 20 },
            }}
          />
        ))}
      </Box>

      {/* ✅ FIXED: Flex instead of Grid – more reliable for even spacing; explicit widths */}
      <Box
        sx={{
          marginLeft: { xs: "32px", sm: "52px" },
          height: height,
          display: "flex",  // ✅ CHANGED BACK: Flex for predictable spacing
          alignItems: "flex-end",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E9EC",
          position: "relative",
          zIndex: 1,
          width: '100%',
          gap: { xs: 0.25, sm: 0.5 },  // Tiny gaps
        }}
      >
        {Array.from({ length: barCount }, (_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              flex: `0 0 ${barWidth}px`,  // ✅ NEW: Fixed basis width, no grow/shrink issues
              minWidth: barWidth,
              maxWidth: barWidth,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={barWidth}
              height={barHeightsPx[i]}
              sx={{ borderRadius: 0 }}
            />
          </Box>
        ))}
      </Box>
      {/* ✅ UPDATED: Labels – match new flex and widths */}
      <Box
        sx={{
          marginLeft: { xs: "32px", sm: "52px" },
          display: "flex",
          justifyContent: isMobile ? "space-around" : "space-between",
          marginTop: "6px",  // Slightly reduced
          width: '100%',
          gap: gapValue,  // Use same dynamic gap for consistency
          alignItems: "center",
          paddingRight: { xs: '8px', sm: 0 },
        }}
      >
        {Array.from({ length: barCount }, (_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: "center",
              flex: `0 0 ${barWidth}px`,
              minWidth: barWidth,
              maxWidth: barWidth,
              width: { xs: `${barWidth - 4}px`, sm: `${barWidth}px` },
            }}
          >
            <Skeleton
              variant="text"
              width="80%"  // ✅ CHANGED: % to fit dynamically
              height={12}
              sx={{
                fontSize: { xs: "7px", sm: "9px" },  // Smaller on mobile
                width: "80%",
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ChartSkeleton;